import React, { forwardRef, ReactElement, Suspense, useEffect, useImperativeHandle, useRef, useState } from 'react'; // we need this to make JSX compile
import * as THREE from 'three';

import { Loader, OrbitControls, PerspectiveCamera } from '@react-three/drei';
import { Canvas, useThree, Vector3 } from '@react-three/fiber';
import { RotateLeftOutlined, SwipeOutlined } from '@mui/icons-material';
import { Chip, IconButton } from '@mui/material';

import { UpdateSwipeabilityEvent } from '../UpdateSwipeabilityEvent';

import '../ProjectPage/ProjectPageStyles.css';

export interface ISceneProps {
    background?: [number, number, number] | [THREE.ColorRepresentation];
    cameraPos?: [number, number, number];
    cameraTargetPos?: Vector3;
    maxZoomDistance?: number;
    models: ReactElement[];
    showMujocoGround?: boolean;
    modelRef?: any;
}
/*
    default model scene:

    [
        <Gamepad pos={[-0.09, -0.30, 0.15]} />,
        <Obstacles pos={[-1.58, -2.2, 0]} euler={[0, 0, 1.57]} />,
        <Car pos={[0, 0, 0.065]} />,
        <LeftHand pos={[-0.1535, -0.35, 0.18]} euler={[0, -1.5, 3.14]} />,
        <RightHand pos={[0.104, -0.35, 0.18]} euler={[0, 1.5, 3.14]} />
    ]
*/

export const Scene = (props: ISceneProps) => {
    const { background, cameraPos = [0, 0, 0], cameraTargetPos = [0, 0, 0], models, showMujocoGround = false } = props;

    const [isHovering, setIsHovering] = useState<boolean>(false);
    const [isSceneInFocus, setIsSceneInFocus] = useState<boolean>(isTouchPrimaryInput());
    const cameraRef = useRef<any>();
    const orbitRef = useRef<any>();

    const performCameraReset = () => {
        const targetPosThreeVec3 = castVec3toThreeVec3(cameraTargetPos);

        cameraRef.current.reset();
        orbitRef.current.target.set(targetPosThreeVec3.x, targetPosThreeVec3.y, targetPosThreeVec3.z);
        orbitRef.current.update();
    }

    const castVec3toThreeVec3: (arg0: Vector3) => THREE.Vector3 = (vecThree: Vector3) => {
        let vecComponents = vecThree.toString().split(",");

        return new THREE.Vector3(+vecComponents[0], +vecComponents[1], +vecComponents[2]);
    }

    const handleHoverStateChange = (newHoverState: boolean) => {
        setIsHovering(newHoverState);

        let update = new UpdateSwipeabilityEvent();
        update.sceneHovering = newHoverState;

        if (!newHoverState && !isTouchPrimaryInput()) {
            setIsSceneInFocus(false);
        }

        window.dispatchEvent(update);
    }

    return (
        <div
            id="canvas-scene-container"
            onTouchStart={() => handleHoverStateChange(true)}
            onTouchCancel={() => handleHoverStateChange(false)}
            onTouchEnd={() => handleHoverStateChange(false)}
            onMouseEnter={() => handleHoverStateChange(true)}
            onMouseLeave={() => handleHoverStateChange(false)}
            onMouseDown={() => setIsSceneInFocus(true)}
            style={{ height: "100%", width: "100%", position: "relative" }} >
            <IconButton aria-label="reset-view" onClick={performCameraReset}>
                <RotateLeftOutlined />
            </IconButton>
            <div className={`interactive-chip-div interactive-chip-div-fade-${isHovering && isSceneInFocus ? "out" : "in"}`}>
                <Chip color="error" icon={<SwipeOutlined />} label="Interactive 3D View" />
            </div>
            <Canvas style={{ width: "100%", height: "100%" }}>
                {/* Setup scene. */}
                <color attach="background" args={background ?? [0.839, 0.922, 1]} />
                <MujocoCamera ref={cameraRef} position={cameraPos}></MujocoCamera>
                <OrbitControls
                    ref={orbitRef}
                    makeDefault
                    enableDamping={false}
                    enablePan={true}
                    enableZoom={isSceneInFocus}
                    enableRotate={true}
                    target={cameraTargetPos}
                    addEventListener={undefined}
                    hasEventListener={undefined}
                    removeEventListener={undefined}
                    dispatchEvent={undefined} />
                <ambientLight intensity={0.1} />
                <directionalLight position={[5, 5, 3]} intensity={0.5} />

                {/* Load models. */}
                <group rotation={[-1.57, 0, 0]}>
                    <>
                        {models.map((model: ReactElement, index: number) => (
                            <Suspense key={`suspense-${index}`} fallback={null}>
                                {model}
                            </Suspense>
                        ))}
                    </>

                    {/* The mujoco ground. */}
                    {showMujocoGround &&
                        <mesh position={[0, 0, 0]}>
                            <planeBufferGeometry attach="geometry" args={[6, 6]} />
                            <meshStandardMaterial
                                attach="material"
                                color={[0.25, 0.26, 0.25]}
                                side={THREE.DoubleSide}
                                opacity={0.5}
                                transparent
                            />
                        </mesh>
                    }
                </group>

            </Canvas><Loader />
        </div>
    );
}

function isTouchPrimaryInput() {
    if (window.matchMedia("(pointer: coarse)").matches) {
        return true;
    }
    return false;
}

interface IMujocoCameraProps {
    position?: [number, number, number];
}

const MujocoCamera = forwardRef((props: IMujocoCameraProps, ref) => {
    const { camera } = useThree();
    const { position } = props;

    useEffect(() => {
        performCameraReset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useImperativeHandle(ref, () => ({
        reset() {
            performCameraReset();
        }
    }));

    const performCameraReset = () => {
        // reset camera position
        camera.near = 0.001;
        camera.far = 1000;
        camera.position.set(
            position?.[0] ?? 0,
            position?.[1] ?? 2,
            position?.[2] ?? 2
        );

        camera.rotateY(60);
        camera.updateProjectionMatrix();
    }
    return <PerspectiveCamera></PerspectiveCamera>;
});